import * as Hooks from 'preact/hooks';
import { useAsyncValues } from '../hooks/useAsync';
import { PropertyValue, PropertySelections, ResolveSelection } from './types';

export function usePriceDeltas(
    getPrice: (
        selections: PropertySelections,
        attachmentProposals: Record<string, string>,
    ) => Promise<number | null>,
    currentPrice: number | undefined,
    resolveSelection: ResolveSelection | undefined,
    attachmentProposals: Record<string, string>,
): {
    priceDeltas: Record<string, Record<string, number>>;
    requestPriceDelta: (propertyName: string, value: PropertyValue) => void;
} {
    const [requestedDeltas, setRequestedDeltas] = Hooks.useState<Array<[string, PropertyValue]>>(
        [],
    );

    Hooks.useEffect(() => {
        setRequestedDeltas([]);
    }, [resolveSelection]);

    const requestPriceDelta = async (property: string, value: PropertyValue) => {
        if (
            requestedDeltas.some(
                ([pendingProperty, pendingValue]) =>
                    property === pendingProperty && value === pendingValue,
            )
        ) {
            return;
        }
        setRequestedDeltas(prev => [...prev, [property, value]]);
    };

    const pricePromises = Hooks.useMemo(() => {
        if (!resolveSelection) return [];

        return requestedDeltas.map(
            async ([property, value]): Promise<[[string, PropertyValue], number | null]> => {
                const selections = await resolveSelection(property, value);
                const price = await getPrice(selections, attachmentProposals);

                return [[property, value], price];
            },
        );
    }, [getPrice, requestedDeltas, resolveSelection, attachmentProposals]);

    const prices = useAsyncValues(pricePromises);

    const priceDeltas = Hooks.useMemo(() => {
        if (currentPrice === undefined || !prices) {
            return {};
        }
        return prices.reduce<Record<string, Record<string, number>>>(
            (priceDeltas, [[property, value], price]) => ({
                ...priceDeltas,
                [property]: {
                    ...(priceDeltas[property] ?? {}),
                    ...(price !== null ? { [value ?? 'null']: price - currentPrice } : {}),
                },
            }),
            {},
        );
    }, [prices, currentPrice]);

    return { requestPriceDelta, priceDeltas };
}
